/*
 * `/reset-password#<token>` (flow F6). The token rides in the fragment, so it never reaches a server log
 * or a Referer. The page reads it once and takes it out of the address bar, so it is not left in the
 * browser's history or on a shared screen; a reload then asks for a new link. A reset ends every session
 * and signs nobody in (OWASP), so success sends the person to sign in.
 */
import { useEffect, useState } from "react";
import {
  LIMITED,
  passwordProblem,
  type ResetOutcome,
  resetOutcome,
  UNREACHABLE,
} from "./account-copy.ts";
import { AuthFrame, NewPasswordFields, postJson } from "./auth-frame.tsx";

/** The token in this page's fragment, or null when it carries none. Reading changes nothing. */
export function fragmentToken(): string | null {
  let token: string;
  try {
    token = decodeURIComponent(location.hash.slice(1)).trim();
  } catch {
    return null;
  }
  return token === "" ? null : token;
}

/** Takes the fragment out of the address bar and the history entry, keeping the page where it is. */
function dropFragment(): void {
  if (location.hash) history.replaceState(history.state, "", location.pathname + location.search);
}

export function ResetPassword() {
  const [token, setToken] = useState(fragmentToken);
  useEffect(dropFragment, []);
  useEffect(() => {
    // A newer reset link opened in this tab changes only the fragment: no new page load.
    const follow = () => {
      const next = fragmentToken();
      if (next === null) return;
      setToken(next);
      setOutcome(null);
      dropFragment();
    };
    window.addEventListener("hashchange", follow);
    return () => window.removeEventListener("hashchange", follow);
  }, []);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<ResetOutcome | null>(null);
  const [waiting, setWaiting] = useState(false);
  useEffect(() => {
    if (outcome?.kind !== "limited") return;
    setWaiting(true);
    const timer = setTimeout(() => setWaiting(false), outcome.retryAfterMs);
    return () => clearTimeout(timer);
  }, [outcome]);

  if (outcome?.kind === "changed")
    return (
      <AuthFrame tab="Password changed" title="Password changed">
        <p className="lcr-signin-sub" role="status">
          Sign in with your new password. Every device that was signed in has been signed out.
        </p>
        <div className="lcr-signin-form">
          <a className="lcr-primary lcr-signin-submit lcr-auth-cta" href="/">
            Sign in
          </a>
        </div>
      </AuthFrame>
    );
  if (token === null || outcome?.kind === "link_dead")
    return (
      <AuthFrame tab="Reset your password" title="This link has expired or was already used">
        <p className="lcr-signin-sub">
          A reset link works once, for 60 minutes. Ask for a new one and use the newest email.
        </p>
        <div className="lcr-signin-form">
          <a className="lcr-primary lcr-signin-submit lcr-auth-cta" href="/forgot-password">
            Send a new link
          </a>
          <a className="lcr-linklike" href="/">
            Back to sign in
          </a>
        </div>
      </AuthFrame>
    );

  const failure =
    problem ??
    (outcome?.kind === "limited" ? LIMITED : outcome?.kind === "unreachable" ? UNREACHABLE : null);
  return (
    <AuthFrame tab="Reset your password" title="Choose a new password">
      <form
        className="lcr-signin-form"
        noValidate
        onSubmit={async (event) => {
          event.preventDefault();
          const found = passwordProblem(password, confirm);
          setProblem(found);
          if (found) return;
          setBusy(true);
          setOutcome(null);
          const answer = await postJson("/api/auth/reset-password", {
            token,
            newPassword: password,
          });
          setOutcome(resetOutcome(answer.status, answer.retryAfter));
          setBusy(false);
        }}
      >
        <NewPasswordFields
          password={password}
          confirm={confirm}
          onPassword={setPassword}
          onConfirm={setConfirm}
          invalid={problem !== null}
          describedBy={failure ? "lcr-reset-failure" : undefined}
        />
        {failure && (
          <p id="lcr-reset-failure" className="lcr-signin-alert" role="alert">
            {failure}
          </p>
        )}
        <button className="lcr-primary lcr-signin-submit" type="submit" disabled={busy || waiting}>
          {busy ? "Saving…" : "Save the new password"}
        </button>
      </form>
    </AuthFrame>
  );
}
